import { NotBoundException } from './exceptions/not-bound-exception.ts';
import { Scope } from './types.ts';
import type { BindingsMap, FactoryFunction } from './types.ts';

export class ServiceContainer<T extends BindingsMap> {
  #rawBindings = new Map<keyof T, { value: FactoryFunction<T, any>; scope: Scope }>();
  #resolvedBindings = new Map<keyof T, T[keyof T]>();

  /**
   * Bind a dependency with a given scope.
   */
  register<Key extends keyof T>(
    binding: Key,
    value: FactoryFunction<T, T[Key]>,
    scope: Scope
  ): void {
    this.#rawBindings.set(binding, { scope, value });
  }

  /**
   * Bind a dependency to the container as a singleton.
   */
  registerSingleton<Key extends keyof T>(
    binding: Key,
    value: FactoryFunction<T, T[Key]>
  ): void {
    this.resetSingleton(binding);

    this.register(binding, value, Scope.Singleton);
  }

  /**
   * Bind a dependency to the container as a factory. Each time the dependency is resolved the container will call the factory function.
   */
  registerFactory<Key extends keyof T>(
    binding: Key,
    value: FactoryFunction<T, T[Key]>
  ): void {
    this.resetSingleton(binding);

    this.register(binding, value, Scope.Factory);
  }

  /**
   * Attempt to resolve a given binding. Will throw if there is no binding found.
   */
  resolve<Key extends keyof T>(binding: Key): T[Key] {
    const bindingConfig = this.#rawBindings.get(binding);

    if (!bindingConfig) {
      throw new NotBoundException(binding.toString());
    }

    const resolvedBinding = this.#resolvedBindings.get(binding);

    if (resolvedBinding) {
      return resolvedBinding as T[Key];
    }

    if (bindingConfig.scope === Scope.Factory) {
      return bindingConfig.value(this);
    }

    const resolved = bindingConfig.value(this);

    this.#resolvedBindings.set(binding, resolved);

    return resolved;
  }

  /**
   * Reset a singleton value. If a value has been previously resolved and is
   * bound as a singleton, this will keep the binding but reset the singleton value
   * until the next resolve.
   */
  resetSingleton<Key extends keyof T>(binding: Key): void {
    this.#resolvedBindings.delete(binding);
  }

  /**
   * Fully remove a binding from the container. Subsequent resolve calls will
   * throw {@link NotBoundException} until the binding is re-registered.
   */
  unregister<Key extends keyof T>(binding: Key): void {
    this.#rawBindings.delete(binding);
    this.#resolvedBindings.delete(binding);
  }
}
